import React, { useState } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Badge,
  ColumnLayout,
  StatusIndicator,
  Table,
  ProgressBar,
  Alert,
  Tabs,
  Grid,
  Button,
  Popover,
  Icon,
  ExpandableSection,
  Pagination,
} from '@cloudscape-design/components';
import CategoryBadge, { CHART_COLOR_BY_CATEGORY } from './CategoryBadge';
import { PieChart } from './ModernizationCharts';
import {
  getTotalServiceSavings,
  isOptimizable,
  getServicePlanType,
} from './PricingCalculatorTab';
import { computeAmortizationBreakdown } from './pricingCalculatorUtils';
import {
  UNMAPPED,
  resolveServiceCategory,
  categoryLabel,
  isAwsSupport,
  serviceARR,
  computeRubiconIndex,
  MODERNIZATION_TARGET_PCT,
} from '../utils/rubiconServiceCategories';
import {
  SAVINGS_PENDING,
  upgradeIsCoveredElsewhere,
  REASON_STORAGE_IOPS,
  REASON_OLA_ASSESSMENT,
  REASON_NO_ARM_RATE,
  REASON_NO_UPGRADE_RATE,
  REASON_NO_SAVING,
  summarizeGravitonSavings,
  serviceComputeOpportunity,
  buildGravitonRecommendation,
  parseConfigSummary,
} from '../utils/gravitonRecommendations';
import { isESCCalculatorUrl, getESCCurrency } from '../utils/escUtils';
import { sanitizeCalculatorUrl } from '../utils/sanitizeCalculatorUrl';
import {
  PAGE_SIZE,
  sortItems,
  paginateItems,
  pageCountOf,
  clampPage,
} from '../utils/tableCollection';

/**
 * Severity -> numeric rank, used only as the Severity column's sortingField.
 *
 * Sorting the label itself puts High < Low < Medium alphabetically, which is worse than
 * not sorting at all. The column shows the label and sorts on this.
 */
const SEVERITY_RANK = { High: 3, Medium: 2, Low: 1 };

/**
 * Recommendation severity -> Cloudscape Badge colour. Read by BOTH the Severity
 * column and the Tags column so a row is one visual unit and the two can never
 * drift apart.
 *
 * Deliberately not CategoryBadge: that is the Cat 1/2/3 modernization scale and
 * would render "Unmapped" on a High-severity row. These are the only Badge colours
 * Cloudscape accepts (blue | grey | green | red | severity-*).
 */
const SEVERITY_COLOR = { High: 'red', Medium: 'blue', Low: 'grey' };

/**
 * Rubicon standalone pricing-calculator review.
 *
 * Deliberately a separate component from PricingCalculatorTab rather than a
 * program-flag inside it: MAP's tab is 2000+ lines of live reviewer path, and
 * Rubicon's semantics differ at almost every point. MAP's file is modified only
 * to add `export` to three pure helpers, which cannot change its behavior.
 *
 * How Rubicon differs from MAP:
 *   - restricts no service and excludes no consumption, so there is no
 *     eligibility column, no always-excluded container and no MAP tab;
 *   - the modernization index comes from the AWS service-category tiers
 *     (Cat 2 + Cat 3 = modern), computed client-side over the FULL calculator
 *     cost rather than from the backend's MAP-qualified pathway data;
 *   - AWS Support is not fundable, so it is removed from every figure and
 *     called out;
 *   - Graviton migration is a first-class recommendation.
 */

// Aggregation copied from PricingCalculatorTab rather than extracted, so MAP's
// render path is untouched. Rubicon's version groups by service code and carries
// the resolved category instead of MAP's eligibility fields.
const aggregateServicesByCode = (services) => {
  if (!services || services.length === 0) return [];

  const grouped = services.reduce((acc, service) => {
    const serviceCode = service.service_code || '';
    const key = serviceCode.toLowerCase();
    if (!acc[key]) {
      acc[key] = {
        service_code: serviceCode,
        // Preserved so the row resolves via the parent when service_code is a
        // per-line vendor code (Bedrock emits service_code='anthropic').
        parent_service_code: service.parent_service_code,
        service_name: service.service_name || '',
        monthly_cost: 0,
        annual_cost: 0,
        count: 0,
        region: service.region,
      };
    }
    acc[key].monthly_cost += service.monthly_cost || 0;
    acc[key].annual_cost += serviceARR(service);
    acc[key].count += 1;
    return acc;
  }, {});

  return Object.values(grouped).map((service) => ({
    ...service,
    rubicon_category: resolveServiceCategory(service),
  }));
};

const RubiconPricingCalculatorTab = ({ request, preloadedData, isESC = false }) => {
  const [activeMainTab, setActiveMainTab] = useState('general');

  // Table sort/page state. Declared here rather than inside the render functions —
  // those are nested, and hooks cannot live in them. Each table gets its own pair so
  // sorting the recommendations does not reset the service-estimates page.
  //
  // Both default to `{}` (no column selected), so the payload's own order is what
  // renders until a header is clicked. See sortItems: Cloudscape's Table never sorts
  // its own items, which is why the previous sortingDisabled={false} did nothing.
  const [serviceSort, setServiceSort] = useState({});
  const [servicePage, setServicePage] = useState(1);
  const [recSort, setRecSort] = useState({});
  const [recPage, setRecPage] = useState(1);
  const calculatorDetails = preloadedData || null;

  const calculatorLinks =
    request?.validation_data?.complete_api_data?.fundRequest?.artifacts?.pricingCalculatorLinks || [];
  const detectedESC =
    isESC || calculatorLinks.some((url) => isESCCalculatorUrl(url)) || calculatorDetails?.is_esc === true;
  const currency = getESCCurrency(detectedESC);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount || 0);

  const services = calculatorDetails?.services || [];
  const calculatorId = calculatorDetails?.calculatorId || calculatorDetails?.calculator_id || 'calculator';

  // ── The ARR chain ─────────────────────────────────────────────────────────
  // Raw total is what the calculator itself reports: every line, upfront in full.
  const rawARR = services.reduce(
    (sum, s) => sum + (s.monthly_cost || 0) * 12 + (s.upfront_cost || 0),
    0,
  );
  const supportServices = services.filter((s) => isAwsSupport(s.service_code));
  const supportRawARR = supportServices.reduce(
    (sum, s) => sum + (s.monthly_cost || 0) * 12 + (s.upfront_cost || 0),
    0,
  );
  // Two-thirds of a 3-year upfront is deferred, matching the backend's
  // amortize_upfront(). AWS Support is excluded so the breakdown reconciles with
  // the Support-excluded ARR chain (see invariant below). The breakdown figures
  // (total upfront, per-year portion counted, deducted excess) are surfaced in
  // the ARR Summary for display transparency (P508436298), matching the MAP tab.
  const {
    totalUpfront: amortizationTotalUpfront,
    perYearPortion: amortizationPerYearPortion,
    deduction: amortizationDeduction,
  } = computeAmortizationBreakdown(services, (s) => !isAwsSupport(s.service_code));

  const modernization = computeRubiconIndex(services);
  // Invariant: rawARR - supportRawARR - amortizationDeduction === modernization.totalARR.
  // Asserted in RubiconPricingCalculatorTab.test.js so the summary can never
  // disagree with the index denominator.

  const riSpSavings = services.reduce((sum, s) => sum + getTotalServiceSavings(s), 0);
  const graviton = summarizeGravitonSavings(services);

  /**
   * Rubicon's "is this service optimizable" test: MAP's isOptimizable plus Graviton.
   *
   * MAP's helper tests the six *_annual_savings fields plus prev_generation,
   * ola_recommended and storage_optimization — it predates the Graviton fields and
   * has no term for them. It cannot be extended in place either:
   * PricingCalculatorTab is MAP's live reviewer path, and a Graviton term there
   * would add rows to MAP's Recommendations tab. So Rubicon wraps it.
   *
   * EVERY consumer of this idea must use THIS one. optimizationOpportunityARR is
   * computed over ALL services, so a narrower filter makes the dashboard count, the
   * CSV rows and the download button disagree with the headline savings figure —
   * a Graviton-only service otherwise reports "No services optimizable" in the same
   * container that says it could move to Graviton.
   *
   * Keyed on the recommendation EXISTING, not on it being priced. An unpriced ARM
   * move is still actionable advice and already appears on the Recommendations tab
   * with its reason string; excluding it is what produced the contradiction above.
   *
   * Evaluated PER LINE ITEM, not via a Set of service_codes. `services` is the raw
   * list, and a real calculator carries many line items per code (963 items across
   * 29 codes in one observed estimate; 75 of them ec2Enhancement). A code-keyed Set
   * would mark an already-Graviton line item optimizable because a *sibling* item
   * sharing its code has an ARM target.
   */
  const isRubiconOptimizable = (s) =>
    isOptimizable(s) || !!buildGravitonRecommendation(s);

  /**
   * The full optimization opportunity: commitment-based savings (RI/SP) composed
   * with Graviton migration. One value feeds BOTH the ARR Summary's "Non-Optimized
   * ARR" and the Optimization Opportunities container's "Potential ARR Savings", so
   * the two can never print different numbers for the same idea.
   *
   * COMPOSED PER SERVICE, NOT SUMMED. RI/SP and Graviton are both measured from the
   * same On-Demand x86 baseline (x86-OD -> x86-committed, and x86-OD -> ARM-OD), so
   * adding them counts their overlap twice. The overlap is per instance, so the
   * composition has to happen per service before summing — see
   * serviceComputeOpportunity, which prefers the backend's exact
   * arm_sp_annual_savings and falls back to the multiplicative formula.
   */
  const optimizationOpportunityARR = services.reduce(
    (sum, s) => sum + serviceComputeOpportunity(s, getTotalServiceSavings(s)),
    0,
  );

  const totalDeductions = supportRawARR + amortizationDeduction + optimizationOpportunityARR;
  const finalRubiconARR = rawARR - totalDeductions;

  const indexMeetsTarget = modernization.index >= MODERNIZATION_TARGET_PCT;

  // Cloudscape's ProgressBar cannot recolour its own bar: `variant` accepts only
  // standalone|flash|key-value, and `status="error"` REPLACES the bar with a status
  // indicator rather than tinting it. So the pass/fail signal is carried by the
  // percentage and by the additionalInfo text — both driven by this one token so
  // they can never disagree.
  const indexColor = indexMeetsTarget ? 'text-status-success' : 'text-status-error';
  const targetVerdict = `${indexMeetsTarget ? 'Meets' : 'Below'} the ${MODERNIZATION_TARGET_PCT}% target`;
  const modernOfTotal = `${formatCurrency(modernization.modernARR)} of ${formatCurrency(modernization.totalARR)} ARR is modern`;

  // ── Recommendations ───────────────────────────────────────────────────────
  // Copied from PricingCalculatorTab.generateRecommendations (a closure inside
  // MAP's component) and extended with Graviton. Rubicon emits no AWS Support
  // recommendation; support is surfaced as a warning instead.
  const generateRecommendations = () => {
    const recommendations = [];
    let id = 1;

    services.forEach((s) => {
      const savings = getTotalServiceSavings(s);

      if (savings > 0) {
        const planType =
          getServicePlanType(s) !== '-'
            ? getServicePlanType(s)
            : 'Standard Reserved Instances (1yr/No Upfront)';
        recommendations.push({
          id: `OPT-${id++}`,
          severity: savings > 10000 ? 'High' : savings > 1000 ? 'Medium' : 'Low',
          service: s.service_name || s.service_code,
          // These rows exist only when the submitted term is On-Demand (the backend
          // gates every RI/SP recommendation on an on-demand term), so naming the
          // current state in the title is accurate, not an assumption — it lets a
          // reviewer see both the issue and the fix in the one column.
          title: `Switch from On-Demand to ${planType}`,
          description: `${s.service_code} in ${s.region || 'us-east-1'} is using On-Demand pricing. Switching to ${planType} would save ${formatCurrency(savings)}/year.`,
          potentialSavings: savings,
          countsTowardTotal: true,
          tags: ['savings_plan'],
        });
      }

      if (s.prev_generation === 'true' && s.recommended_instance_type) {
        // rds_upgrade_annual_savings is the family-upgrade delta ALONE. Three
        // distinct states, which must not collapse into one another:
        //   > 0     -> a real figure
        //   === 0   -> priced it, nothing to gain (the bump is near price-neutral
        //              in some regions and dearer in others)
        //   absent  -> no published rate for the upgraded type
        const upgradeSavings =
          s.rds_upgrade_annual_savings === undefined || s.rds_upgrade_annual_savings === null
            ? null
            : Number(s.rds_upgrade_annual_savings);
        // The upgrade is a fallback lever: the RI figure is priced against the
        // upgraded type, and the Graviton figure changes family too, so either one
        // already contains this delta.
        const coveredElsewhere = upgradeIsCoveredElsewhere(s, savings);

        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Medium',
          service: s.service_name || s.service_code,
          title: `Upgrade to ${s.recommended_instance_type}`,
          description: coveredElsewhere
            ? `Previous-generation instance detected. A larger recommendation on this service already spans the family change — the Reserved Instance figure is priced against ${s.recommended_instance_type}, and a Graviton move changes family too — so this figure is shown for context and is not added to the total.`
            : 'Previous-generation instance detected. Upgrade for better performance and pricing.',
          potentialSavings:
            upgradeSavings === null || upgradeSavings === 0 ? SAVINGS_PENDING : upgradeSavings,
          savingsNote:
            upgradeSavings === null ? REASON_NO_UPGRADE_RATE
              : upgradeSavings === 0 ? REASON_NO_SAVING
                : undefined,
          countsTowardTotal: !coveredElsewhere,
          tags: ['instance_upgrade'],
        });
      }

      if (s.storage_optimization) {
        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Low',
          service: s.service_name || s.service_code,
          title: `Upgrade ${s.service_name || s.service_code} storage type`,
          // Wording note: this must not contain the savings cell's generic fallback
          // string ('Not priced'), or the test asserting that no row falls back to it
          // can never fail — the description text would satisfy the match instead.
          description: `Storage upgrade recommended: ${s.storage_optimization}. Cannot be priced: gp3 costs per-GB plus any provisioned IOPS and throughput above baseline, and the calculator export carries only the storage size and volume type — no IOPS or throughput figure. There is also no storage price source in the pricing pipeline, which fetches compute rates only.`,
          potentialSavings: SAVINGS_PENDING,
          savingsNote: REASON_STORAGE_IOPS,
          countsTowardTotal: true,
          tags: ['storage_upgrade'],
        });
      }

      if (s.ola_recommended === 'true') {
        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Medium',
          service: s.service_name || s.service_code,
          title: 'Optimization and License Assessment (OLA)',
          description:
            'Commercial database detected (Oracle/SQL Server). Recommend an OLA engagement to evaluate licensing optimization. An OLA quantifies licence savings during the engagement, so no figure can be given up front.',
          potentialSavings: SAVINGS_PENDING,
          savingsNote: REASON_OLA_ASSESSMENT,
          countsTowardTotal: true,
          tags: ['ola'],
        });
      }
    });

    graviton.recommendations.forEach((rec) => {
      const priced = rec.annualSavings !== SAVINGS_PENDING;
      // The backend resolves the ARM target against the region's own rate table, so
      // an unpriced recommendation means no ARM rate is published for that
      // region/OS — not that the feature is missing.
      const combined = rec.armSpAnnualSavings;
      recommendations.push({
        id: `GRV-${id++}`,
        severity: priced && rec.annualSavings > 10000 ? 'High' : 'Medium',
        service: rec.serviceName,
        title: `Migrate ${rec.currentInstanceType} to Graviton (${rec.recommendedInstanceType})`,
        description:
          `${rec.currentInstanceType} in ${rec.region || 'us-east-1'} runs on x86. AWS Graviton (${rec.recommendedInstanceType}) delivers better price performance for the same shape.`
          + (combined !== null && combined !== undefined
            ? ` Combined with a Savings Plan on ARM the saving is ${formatCurrency(combined)}/year — that figure supersedes adding this to the commitment recommendation, which would double-count the shared On-Demand baseline.`
            : ''),
        potentialSavings: priced ? rec.annualSavings : SAVINGS_PENDING,
        savingsNote: priced ? undefined : REASON_NO_ARM_RATE,
        // Composed against RI/SP per service in optimizationOpportunityARR rather
        // than added, so it may count here without inflating the headline.
        countsTowardTotal: true,
        tags: ['graviton'],
      });
    });

    // Attach the severity sort rank once, rather than at each of the five push sites
    // above. Only the Severity column's sortingField reads it.
    return recommendations.map((r) => ({
      ...r,
      severityRank: SEVERITY_RANK[r.severity] || 0,
    }));
  };

  const recommendations = generateRecommendations();

  /**
   * Footer totals.
   *
   * `recommendationsTotal` must equal the ARR Summary's Non-Optimized ARR, or the
   * page shows two different answers to the same question. The compute-rate levers
   * (RI/SP, Graviton, family upgrade) are already composed per service in
   * optimizationOpportunityARR, so they are taken from there rather than re-summed.
   * Independent dimensions — storage and licensing — add on top; none carries a
   * figure today, but that is a data gap, not an assumption.
   *
   * `naiveRowSum` is what a reviewer gets adding the visible rows. It is shown only
   * when it disagrees, so the difference is explained rather than discovered.
   */
  const COMPUTE_TAGS = ['savings_plan', 'graviton', 'instance_upgrade'];
  const pricedCounting = recommendations.filter(
    (r) => r.potentialSavings !== SAVINGS_PENDING && r.countsTowardTotal !== false,
  );
  const independentSavings = pricedCounting
    .filter((r) => !r.tags.some((t) => COMPUTE_TAGS.includes(t)))
    .reduce((sum, r) => sum + r.potentialSavings, 0);
  const recommendationsTotal = optimizationOpportunityARR + independentSavings;
  const naiveRowSum = pricedCounting.reduce((sum, r) => sum + r.potentialSavings, 0);

  // Sorted/paginated view of the recommendations. Computed here rather than inside
  // renderRecommendationsTab, which has no block body — and alongside the totals above,
  // which are deliberately over ALL recommendations, not the visible page.
  const sortedRecommendations = sortItems(recommendations, recSort.sortingColumn, recSort.isDescending);
  const recommendationsPage = clampPage(sortedRecommendations, recPage);
  const visibleRecommendations = paginateItems(sortedRecommendations, recommendationsPage);

  // ── Optimization report CSV ───────────────────────────────────────────────
  const downloadOptimizationReport = () => {
    // Must be the Rubicon predicate, not MAP's: the TOTAL row below prints
    // optimizationOpportunityARR, which is summed over ALL services, so any narrower
    // row set makes the Combined Opportunity column fail to add up to it.
    const optimizableServices = services.filter(isRubiconOptimizable);
    const currencySymbol = detectedESC ? '€' : '$';

    // Graviton is read PER LINE ITEM below, deliberately not through a Map keyed by
    // service_code. `services` is the raw list and codes repeat heavily — one observed
    // estimate had 75 ec2Enhancement line items spanning 6 instance types. A code-keyed
    // Map collapses those to one entry (last wins), which both prints another line
    // item's ARM target on this row and breaks the Graviton column's reconciliation
    // with graviton.totalSavings, itself a per-line-item sum.
    //
    // Aggregating the rows instead would destroy the per-line-item instance type,
    // region and config this report exists to carry.

    const headers = [
      'Service Name', 'Service Code', 'Region', 'Rubicon Category',
      'Current Instance Type', 'Recommended Instance Type',
      `Monthly Cost (${currencySymbol})`, `Annual Savings (${currencySymbol})`,
      'Recommended Pricing Strategy', 'Graviton Target', `Graviton Annual Savings (${currencySymbol})`,
      // The RI/SP and Graviton columns overlap, so a reader summing them would
      // overstate. This column is the composed figure and matches the UI headline.
      `Combined Opportunity (${currencySymbol})`,
      'Recommendation Type',
    ];

    const recommendationType = (s) => {
      const types = [];
      if (s.ec2_sp_annual_savings > 0) types.push('EC2 SP');
      if (s.rds_ri_annual_savings > 0) types.push('RDS RI');
      if (s.fargate_sp_annual_savings > 0) types.push('Fargate SP');
      if (s.elasticache_ri_annual_savings > 0) types.push('Elasticache RI');
      if (s.redshift_ri_annual_savings > 0) types.push('Redshift RI');
      if (s.opensearch_ri_annual_savings > 0) types.push('Opensearch RI');
      if (s.prev_generation === 'true') types.push('Instance Upgrade');
      if (s.storage_optimization) types.push('Storage Upgrade');
      if (s.ola_recommended === 'true') types.push('OLA');
      if (buildGravitonRecommendation(s)) types.push('Graviton');
      return types.join(', ') || '-';
    };

    const rows = optimizableServices.map((s) => {
      const grv = buildGravitonRecommendation(s);
      return [
        s.service_name?.trim() || '-',
        s.service_code || '-',
        s.region || '-',
        categoryLabel(resolveServiceCategory(s)),
        parseConfigSummary(s.config_summary, 'Instance Type') ||
          parseConfigSummary(s.config_summary, 'Advance EC2 instance') || s.instance_type || '-',
        s.recommended_instance_type || '-',
        s.monthly_cost?.toFixed(2) || '0.00',
        getTotalServiceSavings(s).toFixed(2),
        getServicePlanType(s),
        grv?.recommendedInstanceType || '-',
        // The reason rather than 0.00 — a zero here would read as "no saving".
        grv ? (grv.annualSavings === SAVINGS_PENDING ? REASON_NO_ARM_RATE : grv.annualSavings.toFixed(2)) : '-',
        serviceComputeOpportunity(s, getTotalServiceSavings(s)).toFixed(2),
        recommendationType(s),
      ];
    });

    rows.push([
      'TOTAL', '', '', '', '', '', '',
      riSpSavings.toFixed(2), '', '', graviton.totalSavings.toFixed(2),
      optimizationOpportunityARR.toFixed(2), '',
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rubicon-optimization-report-${calculatorId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Dashboard Overview ────────────────────────────────────────────────────
  const labelStyle = { fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' };

  const renderArrSummary = () => (
    <Container>
      <Box variant="h3" color="text-status-info">Rubicon ARR Summary</Box>
      <SpaceBetween size="s">
        <div>
          <Box variant="awsui-key-label"><span style={labelStyle}>Calculator Provided ARR</span></Box>
          <Box fontSize="heading-l" fontWeight="bold">{formatCurrency(rawARR)}</Box>
        </div>

        {supportRawARR > 0 && (
          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>AWS Support (not supported)</span></Box>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
              ({formatCurrency(supportRawARR)})
            </Box>
            <Box variant="small" color="text-status-inactive">
              Removed from ARR and the modernization index
            </Box>
          </div>
        )}

        {amortizationDeduction > 0 && (
          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>3-Year Amortization</span></Box>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
              ({formatCurrency(amortizationDeduction)})
            </Box>
            <Box variant="small" color="text-status-inactive">Multi-year upfront ÷ 3</Box>
            {/* Amortization savings breakdown (P508436298): show how the deducted
                excess was derived so the adjustment is transparent, matching MAP. */}
            <Box variant="small" color="text-status-inactive" margin={{ top: 'xxs' }}>
              Total upfront {formatCurrency(amortizationTotalUpfront)} ÷ 3 = {formatCurrency(amortizationPerYearPortion)}/yr counted; excess {formatCurrency(amortizationDeduction)} deducted
            </Box>
          </div>
        )}

        <div>
          <Box variant="awsui-key-label"><span style={labelStyle}>Non-Optimized ARR</span></Box>
          <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
            ({formatCurrency(optimizationOpportunityARR)})
          </Box>
          <Box variant="small" color="text-status-inactive">
            RI/SP + Graviton optimization opportunity
            {riSpSavings > 0 && graviton.totalSavings > 0 && ' (levers compounded, not summed)'}
          </Box>
        </div>

        <div>
          <Box variant="awsui-key-label"><span style={labelStyle}>Optimized Rubicon ARR</span></Box>
          <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
            {formatCurrency(finalRubiconARR)}
          </Box>
        </div>
      </SpaceBetween>
    </Container>
  );

  const renderCategoryBreakdown = () => (
    <SpaceBetween size="xs">
      {[1, 2, 3, UNMAPPED].map((cat) => {
        const bucket = modernization.byCategory[cat];
        if (!bucket || bucket.serviceCount === 0) return null;
        return (
          <div key={String(cat)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CategoryBadge category={cat} />
            <Box variant="small">
              {bucket.pct.toFixed(1)}% · {formatCurrency(bucket.arr)}
            </Box>
          </div>
        );
      })}
    </SpaceBetween>
  );

  const renderModernizationIndex = () => (
    <Container>
      <Box variant="h3" color="text-status-info">Modernization Index</Box>
      {services.length === 0 ? (
        <Alert type="info">No services to analyze.</Alert>
      ) : (
        <SpaceBetween size="s">
          <div>
            <Box variant="awsui-key-label">
              <span style={labelStyle}>Overall Modernization Index</span>
            </Box>
            <Box fontSize="display-l" fontWeight="bold" color={indexColor}>
              {modernization.index.toFixed(1)}%
            </Box>
            {/* additionalInfo is a ReactNode, so the verdict can carry the same
                red/green as the percentage. Verdict first, then the figures. */}
            <ProgressBar
              value={modernization.index}
              additionalInfo={
                <>
                  <Box variant="small" color={indexColor} display="inline">
                    {targetVerdict}
                  </Box>
                  <Box variant="small" color="text-status-inactive" display="inline">
                    {' · '}{modernOfTotal}
                  </Box>
                </>
              }
            />
          </div>

          <div>
            <Box variant="awsui-key-label">
              <span style={labelStyle}>Total Modern ARR (Category 2 + Category 3)</span>
            </Box>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
              {formatCurrency(modernization.modernARR)}
            </Box>
          </div>

          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>Breakdown</span></Box>
            {renderCategoryBreakdown()}
          </div>

          {modernization.unmapped.length > 0 && (
            <Alert type="warning" statusIconAriaLabel="Warning">
              {modernization.unmapped.length} service
              {modernization.unmapped.length === 1 ? '' : 's'} ({formatCurrency(modernization.unmappedARR)})
              could not be categorized. Their ARR still counts in the total but never as modern, so the
              index above is conservative.
            </Alert>
          )}
        </SpaceBetween>
      )}
    </Container>
  );

  const renderOptimizationOpportunities = () => {
    const optimizable = services.filter(isRubiconOptimizable);
    const optimizableNames = optimizable
      .map((s) => s.service_name || s.service_code)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    // Percentage tracks the same combined figure as "Non-Optimized ARR" in the ARR
    // Summary — a % derived from RI/SP alone next to an ARR that includes Graviton
    // would be two different answers to one question.
    const nonOptimizedPct = modernization.totalARR > 0
      ? (optimizationOpportunityARR / modernization.totalARR) * 100
      : 0;

    return (
      <Container>
        <Box variant="h3" color="text-status-info">Optimization Opportunities</Box>
        <SpaceBetween size="s">
          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>Potential ARR Savings</span></Box>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
              {formatCurrency(optimizationOpportunityARR)}
              {graviton.savingsIncomplete && '+'}
            </Box>
            <Box variant="small" color="text-status-inactive">
              {/* Never print "Graviton $0" while figures are unavailable — a zero here
                  reads as "no Graviton saving available", which is the opposite of
                  "not priced". The trailing "+" above carries the same signal.
                  The separator is "·" and not "+" on purpose: the total above is these
                  levers COMPOUNDED, not added, so a "+" would invite the reader to
                  check arithmetic that deliberately does not add up. */}
              RI/SP {formatCurrency(riSpSavings)} · Graviton{' '}
              {graviton.recommendations.length === 0
                ? formatCurrency(0)
                : graviton.totalSavings === 0 && graviton.savingsIncomplete
                  ? 'not priced'
                  : `${formatCurrency(graviton.totalSavings)}${graviton.savingsIncomplete ? ' + more not priced' : ''}`}
              {graviton.totalSavings > 0 && riSpSavings > 0 && ' — compounded, not summed'}
            </Box>
          </div>

          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>Non-Optimized %</span></Box>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
              {nonOptimizedPct.toFixed(1)}%
            </Box>
            <Box variant="small" color="text-status-inactive">of total ARR</Box>
          </div>

          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>Graviton Migration</span></Box>
            {/* Three states, not two. Targets are backend-only now, so keying this on
                recommendations.length alone would report "no x86 instances detected" for
                an estimate full of them whenever the backend has supplied no targets —
                which is every estimate until CR-298688693 deploys. x86Count answers
                "did we see x86?"; recommendations answers "can we name a safe target?" */}
            {graviton.x86Count === 0 && graviton.recommendations.length === 0 ? (
              <Box variant="small" color="text-status-inactive">No x86 instances detected</Box>
            ) : graviton.recommendations.length === 0 ? (
              <SpaceBetween size="xxs">
                <Box color="text-status-info">
                  {graviton.x86Count} x86 instance{graviton.x86Count === 1 ? '' : 's'} detected
                </Box>
                <Box variant="small" color="text-status-inactive">
                  No validated ARM target from the backend. Targets are resolved against the
                  region&apos;s own rate table server-side — the frontend does not guess one.
                </Box>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <Box color="text-status-info">
                  {graviton.recommendations.length} instance
                  {graviton.recommendations.length === 1 ? '' : 's'} could move to Graviton
                </Box>
                {graviton.savingsIncomplete && (
                  // Explicitly NOT rendered as $0, which would read as "no saving".
                  // The backend resolves the ARM target against the region's own rate
                  // table, so an unpriced row means no ARM rate is published there.
                  <Box variant="small" color="text-status-warning">
                    No ARM rate published for {graviton.pendingCount} of{' '}
                    {graviton.recommendations.length} — not priceable in that region/OS
                  </Box>
                )}
              </SpaceBetween>
            )}
          </div>

          <div>
            <Box variant="awsui-key-label"><span style={labelStyle}>Optimizable Services</span></Box>
            <Box color="text-status-info">
              {optimizable.length > 0
                ? `${optimizable.length} optimizable (${optimizableNames.join(', ')})`
                : 'No services optimizable'}
            </Box>
          </div>

          {/* Gated on the same predicate the CSV rows use, so the button appears
              exactly when the report would have data. A Graviton recommendation now
              counts (isRubiconOptimizable), which is why this no longer hides the
              button on a calculator whose only opportunity is an ARM move. */}
          {optimizable.length > 0 && (
            <Button iconName="download" variant="normal" onClick={downloadOptimizationReport}>
              Download Optimization Report
            </Button>
          )}
        </SpaceBetween>
      </Container>
    );
  };

  const renderDashboardOverview = () => (
    <Container header={<Header variant="h2">Dashboard Overview</Header>}>
      <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
        {renderArrSummary()}
        {renderOptimizationOpportunities()}
        {renderModernizationIndex()}
      </Grid>
    </Container>
  );

  // ── General tab ───────────────────────────────────────────────────────────
  const renderServiceEstimates = () => {
    // AWS Support is removed from the table as well as from the figures, so the
    // rows always sum to the ARR the index is computed over.
    const rows = aggregateServicesByCode(services.filter((s) => !isAwsSupport(s.service_code)));

    // Sort, then clamp the page, then slice. The clamp is inline so a stale page index
    // renders page 1 immediately instead of flashing an empty table.
    const sorted = sortItems(rows, serviceSort.sortingColumn, serviceSort.isDescending);
    const page = clampPage(sorted, servicePage);
    const visible = paginateItems(sorted, page);

    return (
      <Container>
        <Table
          columnDefinitions={[
            { id: 'service_name', header: 'Service', cell: (item) => item.service_name, sortingField: 'service_name' },
            { id: 'service_code', header: 'Service Code', cell: (item) => item.service_code, sortingField: 'service_code' },
            {
              id: 'modernization',
              header: 'Modernization',
              cell: (item) => (
                <CategoryBadge
                  category={item.rubicon_category}
                  arr={item.rubicon_category === UNMAPPED ? item.annual_cost : undefined}
                  formatCurrency={formatCurrency}
                />
              ),
            },
            { id: 'monthly_cost', header: 'Monthly Cost', cell: (item) => formatCurrency(item.monthly_cost), sortingField: 'monthly_cost' },
            { id: 'annual_cost', header: 'Annual Cost', cell: (item) => formatCurrency(item.annual_cost), sortingField: 'annual_cost' },
            { id: 'count', header: 'Line Items', cell: (item) => item.count, sortingField: 'count' },
          ]}
          items={visible}
          loadingText="Loading services"
          sortingDisabled={false}
          // Cloudscape's Table is controlled for sorting — without these three props
          // the header renders as clickable and does nothing.
          sortingColumn={serviceSort.sortingColumn}
          sortingDescending={!!serviceSort.isDescending}
          onSortingChange={({ detail }) => {
            setServiceSort({
              sortingColumn: detail.sortingColumn,
              isDescending: detail.isDescending,
            });
            setServicePage(1);
          }}
          empty={<Box textAlign="center" color="inherit"><b>No services found</b></Box>}
          header={
            <Header
              // The TOTAL, deliberately, not the page — a counter that shrank to the
              // page size would read as "we found fewer services".
              counter={`(${rows.length})`}
              description="Services aggregated by service code. AWS Support is excluded — Rubicon does not fund it."
            >
              Service Estimates
            </Header>
          }
          // `pagination` is a ReactNode SLOT. The bare attribute this replaces meant
          // pagination={true}, which React renders as nothing.
          pagination={
            <Pagination
              currentPageIndex={page}
              pagesCount={pageCountOf(sorted)}
              onChange={({ detail }) => setServicePage(detail.currentPageIndex)}
            />
          }
        />
      </Container>
    );
  };

  // ── Modernization tab ─────────────────────────────────────────────────────
  const renderModernizationTab = () => {
    const slices = [1, 2, 3, UNMAPPED]
      .map((cat) => ({
        label: categoryLabel(cat),
        value: modernization.byCategory[cat]?.arr || 0,
        color: CHART_COLOR_BY_CATEGORY[cat],
      }))
      .filter((s) => s.value > 0);

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h3">Modernization Index</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Overall Modernization Index</Box>
              <Box variant="h1" color={indexColor}>
                {modernization.index.toFixed(1)}%
              </Box>
              <ProgressBar
                value={modernization.index}
                additionalInfo={
                  <>
                    <Box variant="small" color={indexColor} display="inline">
                      {targetVerdict}
                    </Box>
                    <Box variant="small" color="text-status-inactive" display="inline">
                      {' · '}{modernOfTotal}
                    </Box>
                  </>
                }
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Total Modern ARR</Box>
              <Box variant="h3" color="text-status-success">
                {formatCurrency(modernization.modernARR)}
              </Box>
              <Box variant="small">
                Category 2 and Category 3 spend. Category 1 (Foundation) is not modern.
              </Box>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h3">Category Breakdown</Header>}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}>
            {slices.length > 0 ? (
              <PieChart size={220} formatCurrency={formatCurrency} slices={slices} />
            ) : (
              <Box color="text-status-inactive">No ARR data available</Box>
            )}
            <SpaceBetween size="s">
              {[1, 2, 3, UNMAPPED].map((cat) => {
                const bucket = modernization.byCategory[cat];
                if (!bucket || bucket.serviceCount === 0) return null;
                return (
                  <div key={String(cat)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 2, background: CHART_COLOR_BY_CATEGORY[cat] }} />
                    <CategoryBadge category={cat} />
                    <Box>
                      {formatCurrency(bucket.arr)} ({bucket.pct.toFixed(1)}%) · {bucket.serviceCount} service
                      {bucket.serviceCount === 1 ? '' : 's'}
                    </Box>
                  </div>
                );
              })}
            </SpaceBetween>
          </div>
        </Container>

        {modernization.unmapped.length > 0 && (
          <Container header={<Header variant="h3">Unmapped Services</Header>}>
            <SpaceBetween size="s">
              <Alert type="warning" statusIconAriaLabel="Warning">
                These service codes are not in Rubicon&apos;s category mapping. Their ARR counts in the
                total but never as modern, so the index is understated until they are classified.
              </Alert>
              <ExpandableSection
                headerText={`${modernization.unmapped.length} unmapped service${modernization.unmapped.length === 1 ? '' : 's'} · ${formatCurrency(modernization.unmappedARR)}`}
              >
                <Table
                  columnDefinitions={[
                    { id: 'code', header: 'Service Code', cell: (i) => i.code },
                    { id: 'name', header: 'Service Name', cell: (i) => i.name },
                    { id: 'arr', header: 'Annual Cost', cell: (i) => formatCurrency(i.arr) },
                  ]}
                  items={modernization.unmapped}
                  variant="embedded"
                />
              </ExpandableSection>
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    );
  };

  // ── Recommendations tab ───────────────────────────────────────────────────
  const renderRecommendationsTab = () => (
    <SpaceBetween size="l">
      {supportServices.length > 0 && (
        <Alert type="warning" header="AWS Support detected" statusIconAriaLabel="Warning">
          This calculator includes AWS Support at {formatCurrency(supportRawARR)} per year. Rubicon does
          not support AWS Support costs — remove it from the estimate. It has been excluded from the ARR
          figures and the modernization index above.
        </Alert>
      )}

      {recommendations.length === 0 ? (
        <Container>
          <Box textAlign="center" color="inherit" padding="xxl">
            <StatusIndicator type="success">
              <Box variant="h3">No Optimization Recommendations</Box>
              <Box variant="p" color="inherit">
                This estimate already uses optimized pricing and modern instance families.
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
      ) : (
        <Container>
          <Table
            columnDefinitions={[
              // No sortingField: the ID is a synthetic sequence (OPT-1, OPT-2, …) and a
              // lexicographic sort puts OPT-10 before OPT-2. An unsortable header beats
              // one that sorts wrongly, and sorting row provenance has no use anyway.
              { id: 'id', header: 'ID', cell: (item) => item.id },
              {
                id: 'severity',
                header: 'Severity',
                // Severity is its own scale — deliberately NOT CategoryBadge,
                // whose labels are the modernization tiers. Colour comes from the
                // shared SEVERITY_COLOR map that the Tags column also reads.
                cell: (item) => (
                  <Badge color={SEVERITY_COLOR[item.severity] || 'grey'}>
                    {item.severity}
                  </Badge>
                ),
                // Sorts on the numeric rank, not the label: alphabetically the labels
                // order High < Low < Medium, which is worse than no sort at all.
                sortingField: 'severityRank',
              },
              { id: 'service', header: 'Service', cell: (item) => item.service, sortingField: 'service' },
              {
                id: 'recommendation',
                header: 'Recommendation',
                cell: (item) => (
                  <Box>
                    <Box variant="strong">{item.title}</Box>
                    {item.description && (
                      <Popover
                        dismissButton={false}
                        position="top"
                        size="medium"
                        triggerType="custom"
                        content={<Box variant="small">{item.description}</Box>}
                      >
                        <Box variant="small" color="text-status-info" display="inline">
                          <Icon name="status-info" size="small" /> Details
                        </Box>
                      </Popover>
                    )}
                  </Box>
                ),
              },
              {
                id: 'savings',
                header: 'Potential Annual Savings',
                cell: (item) =>
                  item.potentialSavings === SAVINGS_PENDING ? (
                    // NEVER $0 here. $0 asserts "we priced it and there is nothing to
                    // gain"; this branch means we could not price it, and the note
                    // says which of the two it is.
                    <Box color="text-status-warning" fontSize="body-s">
                      {item.savingsNote || 'Not priced'}
                    </Box>
                  ) : (
                    <Box color="text-status-success" fontWeight="bold">
                      {formatCurrency(item.potentialSavings)}
                      {item.countsTowardTotal === false && (
                        <Box variant="small" color="text-status-inactive">
                          included in the commitment figure
                        </Box>
                      )}
                    </Box>
                  ),
                sortingField: 'potentialSavings',
              },
              {
                id: 'tags',
                header: 'Tags',
                cell: (item) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.tags.map((tag, index) => (
                      <Badge key={index} color={SEVERITY_COLOR[item.severity] || 'grey'}>
                        {tag}
                      </Badge>
                    ))}
                  </SpaceBetween>
                ),
              },
            ]}
            items={visibleRecommendations}
            loadingText="Loading recommendations"
            sortingDisabled={false}
            sortingColumn={recSort.sortingColumn}
            sortingDescending={!!recSort.isDescending}
            onSortingChange={({ detail }) => {
              setRecSort({
                sortingColumn: detail.sortingColumn,
                isDescending: detail.isDescending,
              });
              setRecPage(1);
            }}
            pagination={
              <Pagination
                currentPageIndex={recommendationsPage}
                pagesCount={pageCountOf(sortedRecommendations)}
                onChange={({ detail }) => setRecPage(detail.currentPageIndex)}
              />
            }
            empty={<Box textAlign="center" color="inherit"><b>No recommendations found</b></Box>}
            header={
              <Header
                counter={`(${recommendations.length})`}
                description="Savings plans, Graviton migration and other cost-reduction opportunities"
              >
                Optimization Recommendations
              </Header>
            }
            footer={
              <Box textAlign="center" padding="s">
                <Box variant="strong">
                  Total Potential Annual Savings: {formatCurrency(recommendationsTotal)}
                </Box>
                {/* The rows above deliberately do not add up to this. Compute-rate
                    levers overlap and are composed, so stating that here is better
                    than letting a reviewer find the discrepancy and distrust both. */}
                {recommendationsTotal < naiveRowSum && (
                  <Box variant="small" color="text-status-inactive">
                    Less than the sum of the rows ({formatCurrency(naiveRowSum)}) because
                    RI/SP and Graviton overlap — both are measured from the same
                    On-Demand baseline, so they compound rather than add.
                  </Box>
                )}
                {/* Grouped by reason: "3 need IOPS data" is a different problem from
                    "5 have no ARM rate", and one line saying "8 pending" hides both. */}
                {Object.entries(
                  recommendations
                    .filter((r) => r.potentialSavings === SAVINGS_PENDING)
                    .reduce((acc, r) => {
                      const reason = r.savingsNote || 'Not priced';
                      acc[reason] = (acc[reason] || 0) + 1;
                      return acc;
                    }, {}),
                ).map(([reason, count]) => (
                  <Box key={reason} variant="small" color="text-status-warning">
                    Excludes {count} recommendation{count === 1 ? '' : 's'}: {reason}
                  </Box>
                ))}
              </Box>
            }
          />
        </Container>
      )}
    </SpaceBetween>
  );

  if (!calculatorDetails || services.length === 0) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="info">
            <Box variant="h3">Pricing Calculator Not Available</Box>
            <Box variant="p" color="inherit" padding={{ top: 's' }}>
              No pricing calculator data is available for this review.
            </Box>
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* An estimate can list hundreds of services and price none of them — the
          backend reads serviceCost with a {} default, so every line becomes 0
          instead of raising. Say so loudly: otherwise every figure below reads
          as a confident $0 for what may be a seven-figure estimate. */}
      {modernization.hasNoCostData && (
        <Alert
          type="error"
          header="This estimate carries no per-service costs"
          statusIconAriaLabel="Error"
        >
          The calculator returned {modernization.serviceCount} service
          {modernization.serviceCount === 1 ? '' : 's'} but no cost for any of them, so
          every figure below is $0 and the modernization index cannot be computed. This is a
          property of the estimate, not of the analysis — the AWS Pricing Calculator export
          contains only service configurations and a single grand total, with no per-service
          pricing. Re-export the estimate from the AWS Pricing Calculator so each service
          carries its own cost, then analyze it again.
        </Alert>
      )}
      {/* EKS / ECS / Move-to-Containers disclaimer.
          Text and gating are DELIBERATELY IDENTICAL to MAP's (PricingCalculatorTab.js)
          — the distortion is the same in both programs, so the two must not drift into
          describing it differently. Pinned against MAP's source in
          RubiconPricingCalculatorTab.test.js.

          It applies to Rubicon just as strongly: EC2 (ec2Enhancement) is Category 1 and
          EKS is Category 2, so worker nodes backing a cluster land in the denominator as
          non-modern and drag the index down.

          Not gated on Fargate on purpose, again matching MAP: Fargate is serverless, so
          there are no EC2 nodes to associate in the first place. */}
      {services.some((s) => /eks|ecs/i.test(s.service_code || '')) && (
        <Alert type="warning" header="Container Workloads - Pricing Estimation Note" statusIconAriaLabel="Warning">
          The AWS Pricing Calculator includes Elastic Kubernetes Service (EKS) and Elastic
          Compute Cloud (EC2) estimations. Chronos cannot automatically associate EC2
          instances with their corresponding EKS clusters. This may result in an inaccurate
          modernization index, as EC2 costs are not included in the modern services ARR.
        </Alert>
      )}
      {renderDashboardOverview()}
      <Tabs
        tabs={[
          { id: 'general', label: 'General', content: renderServiceEstimates() },
          { id: 'modernization', label: 'Modernization', content: renderModernizationTab() },
          { id: 'recommendations', label: 'Recommendations', content: renderRecommendationsTab() },
        ]}
        activeTabId={activeMainTab}
        onChange={({ detail }) => setActiveMainTab(detail.activeTabId)}
        variant="container"
      />
      {(() => {
        // Link back to the estimate, matching MAP's button.
        //
        // The URL is partner-supplied (pricingCalculatorLinks[]) or comes from a query
        // string, and it reaches window.open — a navigation sink. So it is validated AT
        // the sink: sanitizeCalculatorUrl allows only https on the AWS calculator hosts
        // and returns null for everything else, including javascript: and an off-site
        // https host (CWE-601 open redirect). No URL, no button — never a raw bind.
        //
        // Reads the same props path detectedESC already uses, then falls back to the key
        // the standalone response actually sets. MAP's other fallback
        // (pricingData.calculator_url) reads validation_data.pricing_calculator, which is
        // FR-shaped and cannot resolve in the standalone lens, so it is not copied —
        // carrying a dead branch across would imply it does something.
        const calculatorUrl = sanitizeCalculatorUrl(
          calculatorLinks[0] || calculatorDetails?.calculatorUrl,
        );
        if (!calculatorUrl) return null;
        return (
          <Container>
            <Box textAlign="center" padding="m">
              <Button
                variant="primary"
                iconName="external"
                // 'noopener,noreferrer' is a deliberate addition over MAP's call:
                // window.open does NOT imply noopener the way target="_blank" on an
                // anchor does, so without it the opened page can reach window.opener.
                onClick={() => window.open(calculatorUrl, '_blank', 'noopener,noreferrer')}
              >
                Open AWS Pricing Calculator
              </Button>
            </Box>
          </Container>
        );
      })()}
    </SpaceBetween>
  );
};

export default RubiconPricingCalculatorTab;
